import { usePage } from '@inertiajs/react';
import { Box, Container, Typography, Grid, Card, CardContent, Chip, Divider, Button } from '@mui/material';
import {
    CalendarMonth as CalendarIcon,
    LocationOn as LocationIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Person as PersonIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { router } from '@inertiajs/react';

interface TourBooking {
    id: number;
    status: string;
    listing_address: string;
    customer_first_name: string;
    customer_email: string;
    prospect_name: string;
    prospect_email: string;
    prospect_phone: string;
    tour_date: string;
    tour_start_time: string;
    created_at: string;
}

interface Props {
    tour_bookings: TourBooking[];
    [key: string]: any;
}

export default function ViewTourBookingsPage() {
    const { props } = usePage<Props>();
    const { tour_bookings } = props;

    // Parse date string as local date (avoiding UTC timezone issues)
    const parseLocalDate = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
    };

    // Separate upcoming and past tour bookings
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcomingBookings = tour_bookings.filter(booking => {
        const bookingDate = parseLocalDate(booking.tour_date);
        return bookingDate >= today;
    });

    const pastBookings = tour_bookings.filter(booking => {
        const bookingDate = parseLocalDate(booking.tour_date);
        return bookingDate < today;
    }).reverse(); // Most recent past first

    const formatDate = (dateStr: string) => {
        const date = parseLocalDate(dateStr);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatTime = (timeStr: string) => {
        // timeStr is in HH:MM format
        const [hours, minutes] = timeStr.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${minutes} ${ampm}`;
    };

    const formatPhoneNumber = (phone: string | null) => {
        if (!phone) return 'N/A';
        // Remove all non-digit characters
        const digits = phone.replace(/\D/g, '');
        if (digits.length === 10) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} - ${digits.slice(6)}`;
        }
        // If not 10 digits, return original
        return phone;
    };

    const TourBookingCard = ({ booking, isPast }: { booking: TourBooking; isPast: boolean }) => (
        <Card
            sx={{
                mb: 2,
                opacity: isPast ? 0.7 : 1,
                borderLeft: isPast ? '4px solid #9e9e9e' : '4px solid #4caf50',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                }
            }}
        >
            <CardContent>
                {/* Date and Time Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon sx={{ color: isPast ? 'grey.500' : 'primary.main', fontSize: 28 }} />
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: isPast ? 'text.secondary' : 'text.primary' }}>
                                {formatDate(booking.tour_date)}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: isPast ? 'grey.600' : 'success.main' }}>
                                {formatTime(booking.tour_start_time)}
                            </Typography>
                        </Box>
                    </Box>
                    <Chip
                        label={booking.status}
                        size="small"
                        sx={{
                            bgcolor: booking.status === 'scheduled' ? 'success.light' :
                                     booking.status === 'cancelled' ? 'error.light' : 'warning.light',
                            color: booking.status === 'scheduled' ? 'success.dark' :
                                   booking.status === 'cancelled' ? 'error.dark' : 'warning.dark',
                            fontWeight: 600,
                            textTransform: 'capitalize'
                        }}
                    />
                </Box>

                {/* Address */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <LocationIcon sx={{ color: 'error.main', mt: 0.25 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {booking.listing_address}
                    </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Customer Info */}
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                        Landlord
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{booking.customer_first_name}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmailIcon sx={{ color: 'grey.600', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{booking.customer_email}</Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Prospect Info */}
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                        Prospect
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ color: 'info.main', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{booking.prospect_name || 'N/A'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmailIcon sx={{ color: 'grey.600', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{booking.prospect_email || 'N/A'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon sx={{ color: 'grey.600', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{formatPhoneNumber(booking.prospect_phone)}</Typography>
                        </Box>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header with Back Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => router.visit('/admin')}
                    sx={{ color: 'text.secondary' }}
                >
                    Back to Dashboard
                </Button>
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 700, mb: 4 }}>
                Tour Bookings
            </Typography>

            {/* Upcoming Tours */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Upcoming Tours
                    </Typography>
                    <Chip
                        label={`${upcomingBookings.length} ${upcomingBookings.length === 1 ? 'booking' : 'bookings'}`}
                        sx={{ bgcolor: 'success.light', color: 'success.dark', fontWeight: 600 }}
                    />
                </Box>
                {upcomingBookings.length > 0 ? (
                    <Grid container spacing={2}>
                        {upcomingBookings.map(booking => (
                            <Grid item xs={12} md={6} lg={4} key={booking.id}>
                                <TourBookingCard booking={booking} isPast={false} />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Card sx={{ bgcolor: 'grey.50' }}>
                        <CardContent sx={{ textAlign: 'center', py: 4 }}>
                            <CalendarIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                            <Typography color="text.secondary">
                                No upcoming tour bookings
                            </Typography>
                        </CardContent>
                    </Card>
                )}
            </Box>

            {/* Past Tours */}
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Past Tours
                    </Typography>
                    <Chip
                        label={`${pastBookings.length} ${pastBookings.length === 1 ? 'booking' : 'bookings'}`}
                        sx={{ bgcolor: 'grey.200', color: 'grey.700', fontWeight: 600 }}
                    />
                </Box>
                {pastBookings.length > 0 ? (
                    <Grid container spacing={2}>
                        {pastBookings.map(booking => (
                            <Grid item xs={12} md={6} lg={4} key={booking.id}>
                                <TourBookingCard booking={booking} isPast={true} />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Card sx={{ bgcolor: 'grey.50' }}>
                        <CardContent sx={{ textAlign: 'center', py: 4 }}>
                            <CalendarIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                            <Typography color="text.secondary">
                                No past tour bookings
                            </Typography>
                        </CardContent>
                    </Card>
                )}
            </Box>
        </Container>
    );
}

